import type { ReactNode } from 'react'
import { Suspense } from 'react'
import {
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Link,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useHasEnablement } from '@portal/pkg-authorization'
import type { RoleDetailResponse } from '@portal/pkg-api/gen/admin-console'
import { AccessTab } from './AccessTab'
import { UsersTab } from './UsersTab'

export type RoleDetailTab = 'access' | 'users'

interface RoleDetailPageProps {
  role: RoleDetailResponse
  tab: RoleDetailTab
  onTabChange: (tab: RoleDetailTab) => void
  /** The edit drawer, rendered over the page by the route. */
  children?: ReactNode
}

export function RoleDetailPage({ role, tab, onTabChange, children }: RoleDetailPageProps) {
  const { t } = useTranslation('admin-console')
  const navigate = useNavigate()
  const canManage = useHasEnablement('Role', 'MANAGE', { global: true })

  return (
    <Stack
      spacing={2}
      // The app shell clips its content, so each page scrolls itself. The permission grid is
      // long enough that this is the difference between a usable tab and an unreachable one.
      sx={{
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        px: 2,
        pt: 2,
        pb: 2,
      }}
    >
      <Breadcrumbs>
        <Link
          component="button"
          type="button"
          underline="hover"
          onClick={() => void navigate({ to: '/preferences/roles' })}
        >
          {t('roles.page.title')}
        </Link>
        <Typography color="text.primary">{role.name}</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {role.name}
          </Typography>
          {role.description && (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {role.description}
            </Typography>
          )}
        </Box>
        {canManage && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<EditIcon fontSize="small" />}
            onClick={() =>
              void navigate({
                to: '/preferences/roles/$roleId/edit',
                params: { roleId: String(role.id) },
              })
            }
          >
            {t('roles.detail.editDetails')}
          </Button>
        )}
      </Box>

      <Tabs value={tab} onChange={(_event, next: RoleDetailTab) => onTabChange(next)}>
        <Tab value="access" label={t('roles.detail.tabs.access')} />
        <Tab value="users" label={t('roles.detail.tabs.users')} />
      </Tabs>

      <Suspense
        fallback={
          <Stack sx={{ alignItems: 'center', py: 4 }}>
            <CircularProgress />
          </Stack>
        }
      >
        {/*
          Keyed by role id: both tabs hold per-role state - the Access tab's ticked permissions
          most consequentially - and navigating from one role to another reconciles the same
          element rather than remounting it. Without this, role A's selections would be saved as
          a full replacement of role B's.
        */}
        {tab === 'access' ? (
          <AccessTab key={role.id} role={role} />
        ) : (
          <UsersTab key={role.id} role={role} />
        )}
      </Suspense>

      {children}
    </Stack>
  )
}
